<template>
    <div>
        <div class="mb-6 border-b border-gray-200 dark:border-gray-700">
            <nav class="-mb-px flex space-x-8">
                <button
                    @click="activeTab = 'import'"
                    :class="[
                        activeTab === 'import'
                            ? 'border-indigo-500 text-indigo-600 dark:text-indigo-400'
                            : 'border-transparent text-gray-500 hover:border-gray-300 hover:text-gray-700 dark:text-gray-400 dark:hover:text-gray-300',
                        'whitespace-nowrap border-b-2 py-4 px-1 text-sm font-medium transition-colors',
                    ]"
                >
                    <DocumentArrowUpIcon class="mr-2 inline-block h-5 w-5" />
                    Importar Alunos
                </button>
                <button
                    @click="activeTab = 'history'"
                    :class="[
                        activeTab === 'history'
                            ? 'border-indigo-500 text-indigo-600 dark:text-indigo-400'
                            : 'border-transparent text-gray-500 hover:border-gray-300 hover:text-gray-700 dark:text-gray-400 dark:hover:text-gray-300',
                        'whitespace-nowrap border-b-2 py-4 px-1 text-sm font-medium transition-colors',
                    ]"
                >
                    <ClockIcon class="mr-2 inline-block h-5 w-5" />
                    Histórico de Importações
                </button>
            </nav>
        </div>

        <div v-show="activeTab === 'import'">
            <section class="mb-6 bg-white p-6 shadow dark:bg-gray-800 sm:rounded-lg">
                <div class="mb-4 flex items-center justify-between">
                    <div>
                        <h3 class="text-lg font-medium leading-6 text-gray-900 dark:text-white">
                            Importar Alunos Externos
                        </h3>
                        <p class="mt-1 text-sm text-gray-500 dark:text-gray-400">
                            Faça upload de um arquivo CSV para importar alunos para o Mozart.
                        </p>
                    </div>
                    <button
                        @click="downloadTemplate"
                        class="inline-flex items-center rounded-md border border-gray-300 bg-white px-4 py-2 text-sm font-medium text-gray-700 shadow-sm hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-indigo-500 focus:ring-offset-2 dark:border-gray-600 dark:bg-gray-700 dark:text-gray-200 dark:hover:bg-gray-600"
                    >
                        <ArrowDownTrayIcon class="mr-2 h-4 w-4" />
                        Baixar modelo CSV
                    </button>
                </div>

                <form @submit.prevent="uploadFile" class="space-y-4">
                    <div>
                        <label
                            for="file_input"
                            class="mb-2 block text-sm font-medium text-gray-900 dark:text-white"
                        >
                            Arquivo CSV
                        </label>
                        <div
                            v-if="!selectedFile"
                            class="relative flex w-full items-center justify-center"
                            :class="{
                                'border-2 border-dashed border-indigo-400 bg-indigo-50 dark:bg-indigo-900/20': isDragging,
                                'border-2 border-dashed border-gray-300 dark:border-gray-600': !isDragging,
                            }"
                            @dragover.prevent="isDragging = true"
                            @dragleave.prevent="isDragging = false"
                            @drop.prevent="handleDrop"
                        >
                            <label
                                for="file_input"
                                class="flex w-full cursor-pointer flex-col items-center justify-center rounded-lg bg-gray-50 py-6 hover:bg-gray-100 dark:bg-gray-700 dark:hover:bg-gray-600"
                            >
                                <div class="flex flex-col items-center justify-center pb-6 pt-5">
                                    <CloudArrowUpIcon class="mb-3 h-10 w-10 text-gray-400" />
                                    <p class="mb-2 text-sm text-gray-500 dark:text-gray-400">
                                        <span class="font-semibold">Clique para selecionar</span>
                                        ou arraste e solte
                                    </p>
                                    <p class="text-xs text-gray-500 dark:text-gray-400">
                                        Arquivo CSV (máx. 10MB)
                                    </p>
                                </div>
                                <input
                                    id="file_input"
                                    ref="fileInput"
                                    type="file"
                                    accept=".csv"
                                    class="hidden"
                                    @change="handleFileSelect"
                                />
                            </label>
                        </div>

                        <div
                            v-else
                            class="flex items-center justify-between rounded-lg border border-gray-200 bg-gray-50 px-4 py-3 dark:border-gray-700 dark:bg-gray-800"
                        >
                            <div class="flex items-center">
                                <DocumentTextIcon class="mr-3 h-8 w-8 text-gray-400" />
                                <div>
                                    <p class="text-sm font-medium text-gray-900 dark:text-white">
                                        {{ selectedFile.name }}
                                    </p>
                                    <p class="text-xs text-gray-500 dark:text-gray-400">
                                        {{ formatFileSize(selectedFile.size) }}
                                    </p>
                                </div>
                            </div>
                            <button
                                type="button"
                                @click="clearFile"
                                class="rounded-full p-1 text-gray-400 hover:bg-gray-200 hover:text-gray-600 dark:hover:bg-gray-700"
                            >
                                <XMarkIcon class="h-5 w-5" />
                            </button>
                        </div>
                    </div>

                    <div class="flex justify-end">
                        <submit-button
                            :loading="validating"
                            :disabled="!selectedFile"
                        >
                            Validar e Pré-visualizar
                        </submit-button>
                    </div>
                </form>
            </section>

            <section
                v-if="previewData"
                class="mb-6 bg-white p-6 shadow dark:bg-gray-800 sm:rounded-lg"
            >
                <div class="mb-4 flex items-center justify-between">
                    <div>
                        <h3 class="text-lg font-medium leading-6 text-gray-900 dark:text-white">
                            Pré-visualização dos Dados
                        </h3>
                        <p class="mt-1 text-sm text-gray-500 dark:text-gray-400">
                            Revise os dados antes de confirmar a importação.
                        </p>
                    </div>
                    <div class="flex items-center space-x-4">
                        <div class="text-center">
                            <span class="block text-2xl font-bold text-indigo-600 dark:text-indigo-400">
                                {{ previewData.total_records }}
                            </span>
                            <span class="text-xs text-gray-500 dark:text-gray-400">
                                Total de alunos
                            </span>
                        </div>
                        <div v-if="previewData.errors_count > 0" class="text-center">
                            <span class="block text-2xl font-bold text-red-600 dark:text-red-400">
                                {{ previewData.errors_count }}
                            </span>
                            <span class="text-xs text-gray-500 dark:text-gray-400">
                                Com erros
                            </span>
                        </div>
                    </div>
                </div>

                <div
                    v-if="previewData.validation_errors && previewData.validation_errors.length > 0"
                    class="mb-4 rounded-lg border border-red-200 bg-red-50 p-4 dark:border-red-800 dark:bg-red-900/20"
                >
                    <div class="flex items-center">
                        <ExclamationTriangleIcon class="h-5 w-5 text-red-400" />
                        <h4 class="ml-2 text-sm font-medium text-red-800 dark:text-red-200">
                            Erros de Validação
                        </h4>
                    </div>
                    <ul class="mt-2 list-inside list-disc space-y-1 text-sm text-red-700 dark:text-red-300">
                        <li v-for="(error, index) in previewData.validation_errors.slice(0, 5)" :key="index">
                            {{ error }}
                        </li>
                        <li v-if="previewData.validation_errors.length > 5" class="text-red-500">
                            ... e mais {{ previewData.validation_errors.length - 5 }} erro(s)
                        </li>
                    </ul>
                </div>

                <div class="overflow-hidden rounded-lg border border-gray-200 dark:border-gray-700">
                    <div class="bg-gray-50 px-4 py-2 dark:bg-gray-900">
                        <span class="text-xs font-medium uppercase text-gray-500 dark:text-gray-400">
                            Primeiros registros
                        </span>
                    </div>
                    <table class="min-w-full divide-y divide-gray-200 text-sm dark:divide-gray-700">
                        <thead class="bg-gray-50 text-xs uppercase text-gray-500 dark:bg-gray-800 dark:text-gray-300">
                            <tr>
                                <th class="px-4 py-3 text-left">Nome</th>
                                <th class="px-4 py-3 text-left">Email</th>
                                <th class="px-4 py-3 text-left">Data do Pedido</th>
                                <th class="px-4 py-3 text-left">Data de Expiração</th>
                                <th class="px-4 py-3 text-left">Dias de Acesso</th>
                                <th class="px-4 py-3 text-left">Turma</th>
                                <th class="px-4 py-3 text-left">Status</th>
                            </tr>
                        </thead>
                        <tbody class="divide-y divide-gray-200 bg-white dark:divide-gray-700 dark:bg-gray-800">
                            <tr
                                v-for="(row, index) in previewData.first_rows"
                                :key="'first-' + index"
                                :class="row.has_error ? 'bg-red-50 dark:bg-red-900/10' : ''"
                            >
                                <td class="px-4 py-3 text-gray-900 dark:text-gray-100">
                                    {{ row.name }}
                                </td>
                                <td class="px-4 py-3 text-gray-700 dark:text-gray-300">
                                    {{ row.email }}
                                </td>
                                <td class="px-4 py-3 text-gray-700 dark:text-gray-300">
                                    {{ row.order_date }}
                                </td>
                                <td class="px-4 py-3 text-gray-700 dark:text-gray-300">
                                    {{ row.expiration_date }}
                                </td>
                                <td class="px-4 py-3 text-gray-700 dark:text-gray-300">
                                    {{ row.days_access }}
                                </td>
                                <td class="px-4 py-3 text-gray-700 dark:text-gray-300">
                                    {{ row.classroom_name }}
                                </td>
                                <td class="px-4 py-3">
                                    <span
                                        v-if="row.has_error"
                                        class="inline-flex rounded bg-red-100 px-2 py-0.5 text-xs font-medium text-red-700 dark:bg-red-900/40 dark:text-red-200"
                                        v-tooltip="row.error_message"
                                    >
                                        Erro
                                    </span>
                                    <span
                                        v-else
                                        class="inline-flex rounded bg-green-100 px-2 py-0.5 text-xs font-medium text-green-700 dark:bg-green-900/30 dark:text-green-200"
                                    >
                                        OK
                                    </span>
                                </td>
                            </tr>
                        </tbody>
                    </table>

                    <template v-if="previewData.last_rows && previewData.last_rows.length > 0">
                        <div class="border-t border-gray-200 bg-gray-50 px-4 py-2 dark:border-gray-700 dark:bg-gray-900">
                            <span class="text-xs font-medium uppercase text-gray-500 dark:text-gray-400">
                                ... {{ previewData.total_records - 20 }} registros omitidos ...
                            </span>
                        </div>
                        <div class="bg-gray-50 px-4 py-2 dark:bg-gray-900">
                            <span class="text-xs font-medium uppercase text-gray-500 dark:text-gray-400">
                                Últimos registros
                            </span>
                        </div>
                        <table class="min-w-full divide-y divide-gray-200 text-sm dark:divide-gray-700">
                            <tbody class="divide-y divide-gray-200 bg-white dark:divide-gray-700 dark:bg-gray-800">
                                <tr
                                    v-for="(row, index) in previewData.last_rows"
                                    :key="'last-' + index"
                                    :class="row.has_error ? 'bg-red-50 dark:bg-red-900/10' : ''"
                                >
                                    <td class="px-4 py-3 text-gray-900 dark:text-gray-100">
                                        {{ row.name }}
                                    </td>
                                    <td class="px-4 py-3 text-gray-700 dark:text-gray-300">
                                        {{ row.email }}
                                    </td>
                                    <td class="px-4 py-3 text-gray-700 dark:text-gray-300">
                                        {{ row.order_date }}
                                    </td>
                                    <td class="px-4 py-3 text-gray-700 dark:text-gray-300">
                                        {{ row.expiration_date }}
                                    </td>
                                    <td class="px-4 py-3 text-gray-700 dark:text-gray-300">
                                        {{ row.days_access }}
                                    </td>
                                    <td class="px-4 py-3 text-gray-700 dark:text-gray-300">
                                        {{ row.classroom_name }}
                                    </td>
                                    <td class="px-4 py-3">
                                        <span
                                            v-if="row.has_error"
                                            class="inline-flex rounded bg-red-100 px-2 py-0.5 text-xs font-medium text-red-700 dark:bg-red-900/40 dark:text-red-200"
                                            v-tooltip="row.error_message"
                                        >
                                            Erro
                                        </span>
                                        <span
                                            v-else
                                            class="inline-flex rounded bg-green-100 px-2 py-0.5 text-xs font-medium text-green-700 dark:bg-green-900/30 dark:text-green-200"
                                        >
                                            OK
                                        </span>
                                    </td>
                                </tr>
                            </tbody>
                        </table>
                    </template>
                </div>

                <div class="mt-6 flex items-center justify-end space-x-3">
                    <button
                        type="button"
                        @click="cancelPreview"
                        class="rounded-md border border-gray-300 bg-white px-4 py-2 text-sm font-medium text-gray-700 shadow-sm hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-indigo-500 focus:ring-offset-2 dark:border-gray-600 dark:bg-gray-700 dark:text-gray-200 dark:hover:bg-gray-600"
                    >
                        Cancelar
                    </button>
                    <submit-button
                        :loading="importing"
                        :disabled="previewData.errors_count > 0 && !forceImport"
                        @click="confirmImport"
                    >
                        <CheckIcon class="mr-2 h-4 w-4" />
                        Confirmar Importação
                    </submit-button>
                </div>

                <div v-if="previewData.errors_count > 0" class="mt-4 flex items-center">
                    <input
                        id="force_import"
                        v-model="forceImport"
                        type="checkbox"
                        class="h-4 w-4 rounded border-gray-300 text-indigo-600 focus:ring-indigo-500"
                    />
                    <label for="force_import" class="ml-2 text-sm text-gray-600 dark:text-gray-400">
                        Importar apenas registros válidos (ignorar {{ previewData.errors_count }} com erros)
                    </label>
                </div>
            </section>
        </div>

        <div v-show="activeTab === 'history'">
            <filters
                class="mb-4"
                v-model="historyFilters"
                date-filter
                @fetch="
                    historyPagination.current_page = 1;
                    fetchHistory();
                "
            />

            <table-component
                :headers="historyHeaders"
                :loading="loadingHistory"
                :pagination="historyPagination"
                @fetch="fetchHistory"
            >
                <tr v-for="item in historyItems" :key="item.id">
                    <td class="table-cell-sm">
                        <div class="font-medium text-gray-900 dark:text-gray-100">
                            #{{ item.id }}
                        </div>
                        <div class="text-xs text-gray-500 dark:text-gray-400">
                            {{ formatDateTime(item.created_at) }}
                        </div>
                    </td>
                    <td class="table-cell-sm text-gray-700 dark:text-gray-300">
                        {{ item.responsible?.name || 'Sistema' }}
                    </td>
                    <td class="table-cell-sm text-gray-700 dark:text-gray-300">
                        {{ item.total_records }}
                    </td>
                    <td class="table-cell-sm">
                        <span class="text-green-600 dark:text-green-400">
                            {{ item.success_count }}
                        </span>
                    </td>
                    <td class="table-cell-sm">
                        <span class="text-red-600 dark:text-red-400">
                            {{ item.error_count }}
                        </span>
                    </td>
                    <td class="table-cell-sm">
                        <span :class="statusClass(item.status)">
                            {{ statusLabel(item.status) }}
                        </span>
                    </td>
                    <td class="table-cell-sm">
                        <div class="flex space-x-2">
                            <button
                                @click="openDetailsModal(item)"
                                class="text-indigo-600 hover:text-indigo-800 dark:text-indigo-400 dark:hover:text-indigo-300"
                                v-tooltip="'Ver detalhes'"
                            >
                                <EyeIcon class="h-5 w-5" />
                            </button>
                            <button
                                v-if="item.error_count > 0"
                                @click="downloadErrorReport(item.id)"
                                :disabled="downloadingReport === item.id"
                                class="text-gray-600 hover:text-gray-800 dark:text-gray-400 dark:hover:text-gray-300"
                                v-tooltip="'Baixar relatório de erros'"
                            >
                                <ArrowDownTrayIcon
                                    v-if="downloadingReport !== item.id"
                                    class="h-5 w-5"
                                />
                                <svg
                                    v-else
                                    class="h-5 w-5 animate-spin"
                                    viewBox="0 0 24 24"
                                >
                                    <circle
                                        class="opacity-25"
                                        cx="12"
                                        cy="12"
                                        r="10"
                                        stroke="currentColor"
                                        stroke-width="4"
                                    />
                                    <path
                                        class="opacity-75"
                                        fill="currentColor"
                                        d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4z"
                                    />
                                </svg>
                            </button>
                        </div>
                    </td>
                </tr>
            </table-component>
        </div>

        <TransitionRoot as="template" :show="detailsModalOpen">
            <Dialog as="div" class="relative z-50" @close="closeDetailsModal">
                <TransitionChild
                    as="template"
                    enter="ease-out duration-200"
                    enter-from="opacity-0"
                    enter-to="opacity-100"
                    leave="ease-in duration-150"
                    leave-from="opacity-100"
                    leave-to="opacity-0"
                >
                    <div class="fixed inset-0 bg-black/40" />
                </TransitionChild>

                <div class="fixed inset-0 z-50 overflow-y-auto">
                    <div class="flex min-h-full items-center justify-center p-4">
                        <TransitionChild
                            as="template"
                            enter="ease-out duration-200"
                            enter-from="opacity-0 translate-y-4 sm:translate-y-0 sm:scale-95"
                            enter-to="opacity-100 translate-y-0 sm:scale-100"
                            leave="ease-in duration-150"
                            leave-from="opacity-100 translate-y-0 sm:scale-100"
                            leave-to="opacity-0 translate-y-4 sm:translate-y-0 sm:scale-95"
                        >
                            <DialogPanel class="w-full max-w-4xl overflow-hidden rounded-lg bg-white shadow-xl dark:bg-gray-800">
                                <div class="border-b border-gray-200 bg-gray-50 px-6 py-4 dark:border-gray-700 dark:bg-gray-900">
                                    <DialogTitle class="text-lg font-medium text-gray-900 dark:text-white">
                                        Detalhes da Importação #{{ selectedImport?.id }}
                                    </DialogTitle>
                                </div>

                                <div class="px-6 py-6" v-if="selectedImport">
                                    <div class="mb-6 grid gap-4 sm:grid-cols-2 lg:grid-cols-4">
                                        <div class="rounded-lg border border-gray-200 bg-gray-50 p-4 dark:border-gray-700 dark:bg-gray-900">
                                            <span class="text-xs font-medium uppercase text-gray-500 dark:text-gray-400">
                                                Data/Hora
                                            </span>
                                            <p class="mt-1 text-sm font-semibold text-gray-900 dark:text-white">
                                                {{ formatDateTime(selectedImport.created_at) }}
                                            </p>
                                        </div>
                                        <div class="rounded-lg border border-gray-200 bg-gray-50 p-4 dark:border-gray-700 dark:bg-gray-900">
                                            <span class="text-xs font-medium uppercase text-gray-500 dark:text-gray-400">
                                                Responsável
                                            </span>
                                            <p class="mt-1 text-sm font-semibold text-gray-900 dark:text-white">
                                                {{ selectedImport.responsible?.name || 'Sistema' }}
                                            </p>
                                        </div>
                                        <div class="rounded-lg border border-gray-200 bg-gray-50 p-4 dark:border-gray-700 dark:bg-gray-900">
                                            <span class="text-xs font-medium uppercase text-gray-500 dark:text-gray-400">
                                                Produtor
                                            </span>
                                            <p class="mt-1 text-sm font-semibold text-gray-900 dark:text-white">
                                                {{ selectedImport.producer?.name || '-' }}
                                            </p>
                                        </div>
                                        <div class="rounded-lg border border-gray-200 bg-gray-50 p-4 dark:border-gray-700 dark:bg-gray-900">
                                            <span class="text-xs font-medium uppercase text-gray-500 dark:text-gray-400">
                                                Total de Registros
                                            </span>
                                            <p class="mt-1 text-sm font-semibold text-gray-900 dark:text-white">
                                                {{ selectedImport.total_records }}
                                            </p>
                                        </div>
                                    </div>

                                    <div class="mb-6 grid gap-4 sm:grid-cols-3">
                                        <div class="flex items-center rounded-lg border border-green-200 bg-green-50 p-4 dark:border-green-800 dark:bg-green-900/20">
                                            <CheckCircleIcon class="h-8 w-8 text-green-500" />
                                            <div class="ml-3">
                                                <span class="text-xs font-medium uppercase text-green-600 dark:text-green-400">
                                                    Sucesso
                                                </span>
                                                <p class="text-2xl font-bold text-green-700 dark:text-green-300">
                                                    {{ selectedImport.success_count }}
                                                </p>
                                            </div>
                                        </div>
                                        <div class="flex items-center rounded-lg border border-red-200 bg-red-50 p-4 dark:border-red-800 dark:bg-red-900/20">
                                            <XCircleIcon class="h-8 w-8 text-red-500" />
                                            <div class="ml-3">
                                                <span class="text-xs font-medium uppercase text-red-600 dark:text-red-400">
                                                    Erros
                                                </span>
                                                <p class="text-2xl font-bold text-red-700 dark:text-red-300">
                                                    {{ selectedImport.error_count }}
                                                </p>
                                            </div>
                                        </div>
                                        <div class="flex items-center rounded-lg border border-gray-200 bg-gray-50 p-4 dark:border-gray-700 dark:bg-gray-900">
                                            <ClockIcon class="h-8 w-8 text-gray-500" />
                                            <div class="ml-3">
                                                <span class="text-xs font-medium uppercase text-gray-600 dark:text-gray-400">
                                                    Status
                                                </span>
                                                <p class="text-lg font-semibold" :class="statusTextClass(selectedImport.status)">
                                                    {{ statusLabel(selectedImport.status) }}
                                                </p>
                                            </div>
                                        </div>
                                    </div>

                                    <div>
                                        <h4 class="mb-3 text-sm font-semibold text-gray-900 dark:text-white">
                                            Registros Importados
                                        </h4>
                                        <div class="max-h-64 overflow-auto rounded-lg border border-gray-200 dark:border-gray-700">
                                            <table class="min-w-full divide-y divide-gray-200 text-sm dark:divide-gray-700">
                                                <thead class="sticky top-0 bg-gray-50 text-xs uppercase text-gray-500 dark:bg-gray-800 dark:text-gray-300">
                                                    <tr>
                                                        <th class="px-4 py-2 text-left">Nome</th>
                                                        <th class="px-4 py-2 text-left">Email</th>
                                                        <th class="px-4 py-2 text-left">Turma</th>
                                                        <th class="px-4 py-2 text-left">Status</th>
                                                        <th class="px-4 py-2 text-left">Mensagem</th>
                                                    </tr>
                                                </thead>
                                                <tbody class="divide-y divide-gray-200 bg-white dark:divide-gray-700 dark:bg-gray-800">
                                                    <tr
                                                        v-for="item in selectedImport.items"
                                                        :key="item.id"
                                                        :class="item.status === 'error' ? 'bg-red-50 dark:bg-red-900/10' : ''"
                                                    >
                                                        <td class="px-4 py-2 text-gray-900 dark:text-gray-100">
                                                            {{ item.name }}
                                                        </td>
                                                        <td class="px-4 py-2 text-gray-700 dark:text-gray-300">
                                                            {{ item.email }}
                                                        </td>
                                                        <td class="px-4 py-2 text-gray-700 dark:text-gray-300">
                                                            {{ item.classroom_name }}
                                                        </td>
                                                        <td class="px-4 py-2">
                                                            <span :class="itemStatusClass(item.status)">
                                                                {{ itemStatusLabel(item.status) }}
                                                            </span>
                                                        </td>
                                                        <td class="max-w-xs truncate px-4 py-2 text-xs text-gray-500 dark:text-gray-400">
                                                            {{ item.error_message || '-' }}
                                                        </td>
                                                    </tr>
                                                </tbody>
                                            </table>
                                        </div>
                                    </div>
                                </div>

                                <div class="flex justify-end gap-2 border-t border-gray-200 bg-gray-50 px-6 py-4 dark:border-gray-700 dark:bg-gray-900">
                                    <button
                                        v-if="selectedImport?.error_count > 0"
                                        type="button"
                                        @click="downloadErrorReport(selectedImport.id)"
                                        class="inline-flex items-center rounded-md border border-gray-300 bg-white px-4 py-2 text-sm font-medium text-gray-700 shadow-sm hover:bg-gray-50 dark:border-gray-600 dark:bg-gray-700 dark:text-gray-200 dark:hover:bg-gray-600"
                                    >
                                        <ArrowDownTrayIcon class="mr-2 h-4 w-4" />
                                        Baixar Relatório de Erros
                                    </button>
                                    <button
                                        type="button"
                                        @click="closeDetailsModal"
                                        class="inline-flex items-center rounded-md bg-indigo-600 px-4 py-2 text-sm font-medium text-white shadow-sm hover:bg-indigo-700"
                                    >
                                        Fechar
                                    </button>
                                </div>
                            </DialogPanel>
                        </TransitionChild>
                    </div>
                </div>
            </Dialog>
        </TransitionRoot>
    </div>
</template>

<script>
import {
    Dialog,
    DialogPanel,
    DialogTitle,
    TransitionChild,
    TransitionRoot,
} from "@headlessui/vue";
import {
    DocumentArrowUpIcon,
    ClockIcon,
    ArrowDownTrayIcon,
    CloudArrowUpIcon,
    DocumentTextIcon,
    XMarkIcon,
    CheckIcon,
    ExclamationTriangleIcon,
    EyeIcon,
    CheckCircleIcon,
    XCircleIcon,
} from "@heroicons/vue/24/outline";
import { useToast } from "vue-toastification";
import Filters from "../../../components/layouts/Filters.vue";
import TableComponent from "../../../components/layouts/table/TableComponent.vue";
import SubmitButton from "../../../components/forms/SubmitButton.vue";

export default {
    components: {
        Dialog,
        DialogPanel,
        DialogTitle,
        TransitionChild,
        TransitionRoot,
        DocumentArrowUpIcon,
        ClockIcon,
        ArrowDownTrayIcon,
        CloudArrowUpIcon,
        DocumentTextIcon,
        XMarkIcon,
        CheckIcon,
        ExclamationTriangleIcon,
        EyeIcon,
        CheckCircleIcon,
        XCircleIcon,
        Filters,
        TableComponent,
        SubmitButton,
    },

    props: ["user"],

    setup() {
        const toast = useToast();
        return { toast };
    },

    data() {
        return {
            activeTab: "import",
            isDragging: false,
            selectedFile: null,
            validating: false,
            importing: false,
            previewData: null,
            forceImport: false,

            loadingHistory: false,
            historyItems: [],
            historyFilters: {},
            historyPagination: {
                total: 0,
                per_page: 10,
                current_page: 1,
                last_page: 1,
                from: 1,
                to: 10,
            },
            historyHeaders: [
                "ID",
                "Responsável",
                "Quantidade de alunos",
                "Sucesso",
                "Erros",
                "Status",
                "Ações",
            ],

            detailsModalOpen: false,
            selectedImport: null,
            downloadingReport: null,
        };
    },

    mounted() {
        this.fetchHistory();
    },

    methods: {
        handleFileSelect(event) {
            const file = event.target.files[0];
            if (file) {
                this.validateAndSetFile(file);
            }
        },

        handleDrop(event) {
            this.isDragging = false;
            const file = event.dataTransfer.files[0];
            if (file) {
                this.validateAndSetFile(file);
            }
        },

        validateAndSetFile(file) {
            const allowedExtensions = [".csv"];
            const fileName = file.name.toLowerCase();
            const fileExtension = fileName.substring(fileName.lastIndexOf("."));

            if (!allowedExtensions.includes(fileExtension)) {
                this.toast.error("Tipo de arquivo inválido. Use apenas CSV.");
                return;
            }

            if (file.size > 10 * 1024 * 1024) {
                this.toast.error("O arquivo deve ter no máximo 10MB.");
                return;
            }

            this.selectedFile = file;
            this.previewData = null;
        },

        clearFile() {
            this.selectedFile = null;
            this.previewData = null;
            this.$nextTick(() => {
                if (this.$refs.fileInput) {
                    this.$refs.fileInput.value = "";
                }
            });
        },

        formatFileSize(bytes) {
            if (bytes === 0) return "0 Bytes";
            const k = 1024;
            const sizes = ["Bytes", "KB", "MB", "GB"];
            const i = Math.floor(Math.log(bytes) / Math.log(k));
            return parseFloat((bytes / Math.pow(k, i)).toFixed(2)) + " " + sizes[i];
        },

        async uploadFile() {
            if (!this.selectedFile) {
                this.toast.error("Selecione um arquivo para importar.");
                return;
            }

            this.validating = true;

            try {
                const formData = new FormData();
                formData.append("file", this.selectedFile);
                formData.append("user_id", this.user.id);

                const { data } = await axios.post(
                    `/api/user/${this.user.id}/student-imports/validate`,
                    formData,
                    { headers: { "Content-Type": "multipart/form-data" } }
                );

                this.previewData = data.data;
                this.toast.success("Arquivo validado com sucesso!");
            } catch (error) {
                const message = error.response?.status === 403
                    ? "Você não possui permissão para importar alunos."
                    : (error.response?.data?.message || "Erro ao validar arquivo.");
                this.toast.error(message);
            } finally {
                this.validating = false;
            }
        },

        cancelPreview() {
            this.previewData = null;
            this.forceImport = false;
        },

        async confirmImport() {
            this.importing = true;

            try {
                const formData = new FormData();
                formData.append("file", this.selectedFile);
                formData.append("user_id", this.user.id);
                formData.append("force", this.forceImport ? "1" : "0");

                const { data } = await axios.post(
                    `/api/user/${this.user.id}/student-imports`,
                    formData,
                    { headers: { "Content-Type": "multipart/form-data" } }
                );

                this.toast.success(
                    `Importação iniciada! ${data.data.total_records} registros em processamento.`
                );

                this.clearFile();
                this.previewData = null;
                this.forceImport = false;
                this.activeTab = "history";
                this.fetchHistory();
            } catch (error) {
                const message = error.response?.status === 403
                    ? "Você não possui permissão para importar alunos."
                    : (error.response?.data?.message || "Erro ao processar importação.");
                this.toast.error(message);
            } finally {
                this.importing = false;
            }
        },

        downloadTemplate() {
            const headers = "name,email,order_date,expiration_date,days_access,classroom_name";
            const example1 = "João Silva,joao@email.com,2025-11-01,,365,Turma Avançada";
            const example2 = "Maria Souza,maria@email.com,2025-11-03,2026-11-03,,Turma Intermediária";

            const csvContent = [headers, example1, example2].join("\n");
            const blob = new Blob([csvContent], { type: "text/csv;charset=utf-8;" });
            const link = document.createElement("a");
            const url = URL.createObjectURL(blob);

            link.setAttribute("href", url);
            link.setAttribute("download", "modelo_importacao_alunos.csv");
            link.style.visibility = "hidden";
            document.body.appendChild(link);
            link.click();
            document.body.removeChild(link);
        },

        async fetchHistory() {
            this.loadingHistory = true;

            try {
                const { data } = await axios.get(
                    `/api/user/${this.user.id}/student-imports`,
                    {
                        params: {
                            page: this.historyPagination.current_page,
                            ...this.historyFilters,
                        },
                    }
                );

                this.historyItems = data.data;
                this.historyPagination = {
                    total: data.total,
                    per_page: data.per_page,
                    current_page: data.current_page,
                    last_page: data.last_page,
                    from: data.from,
                    to: data.to,
                };
            } catch (error) {
                if (error.response?.status === 403) {
                    this.toast.error("Você não possui permissão para acessar as importações de alunos.");
                } else {
                    this.toast.error("Erro ao carregar histórico de importações.");
                }
            } finally {
                this.loadingHistory = false;
            }
        },

        async openDetailsModal(item) {
            try {
                const { data } = await axios.get(
                    `/api/user/${this.user.id}/student-imports/${item.id}`
                );
                this.selectedImport = data.data;
                this.detailsModalOpen = true;
            } catch (error) {
                this.toast.error("Erro ao carregar detalhes da importação.");
            }
        },

        closeDetailsModal() {
            this.detailsModalOpen = false;
            this.selectedImport = null;
        },

        async downloadErrorReport(importId) {
            this.downloadingReport = importId;

            try {
                const response = await axios.get(
                    `/api/user/${this.user.id}/student-imports/${importId}/errors`,
                    { responseType: "blob" }
                );

                const blob = new Blob([response.data], { type: "text/csv" });
                const link = document.createElement("a");
                link.href = URL.createObjectURL(blob);
                link.download = `erros_importacao_${importId}.csv`;
                link.click();

                this.toast.success("Relatório de erros baixado com sucesso!");
            } catch (error) {
                this.toast.error("Erro ao baixar relatório de erros.");
            } finally {
                this.downloadingReport = null;
            }
        },

        formatDateTime(dateString) {
            if (!dateString) return "-";
            return new Date(dateString).toLocaleString("pt-BR", {
                day: "2-digit",
                month: "2-digit",
                year: "numeric",
                hour: "2-digit",
                minute: "2-digit",
            });
        },

        statusLabel(status) {
            const labels = {
                pending: "Pendente",
                processing: "Processando",
                completed: "Concluído",
                completed_with_errors: "Concluído com erros",
                failed: "Falha",
            };
            return labels[status] || status;
        },

        statusClass(status) {
            const classes = {
                pending: "inline-flex rounded bg-yellow-100 px-2 py-0.5 text-xs font-medium text-yellow-800 dark:bg-yellow-900/30 dark:text-yellow-200",
                processing: "inline-flex rounded bg-blue-100 px-2 py-0.5 text-xs font-medium text-blue-800 dark:bg-blue-900/30 dark:text-blue-200",
                completed: "inline-flex rounded bg-green-100 px-2 py-0.5 text-xs font-medium text-green-800 dark:bg-green-900/30 dark:text-green-200",
                completed_with_errors: "inline-flex rounded bg-orange-100 px-2 py-0.5 text-xs font-medium text-orange-800 dark:bg-orange-900/30 dark:text-orange-200",
                failed: "inline-flex rounded bg-red-100 px-2 py-0.5 text-xs font-medium text-red-800 dark:bg-red-900/30 dark:text-red-200",
            };
            return classes[status] || classes.pending;
        },

        statusTextClass(status) {
            const classes = {
                pending: "text-yellow-600 dark:text-yellow-400",
                processing: "text-blue-600 dark:text-blue-400",
                completed: "text-green-600 dark:text-green-400",
                completed_with_errors: "text-orange-600 dark:text-orange-400",
                failed: "text-red-600 dark:text-red-400",
            };
            return classes[status] || classes.pending;
        },

        itemStatusLabel(status) {
            const labels = {
                pending: "Pendente",
                success: "Sucesso",
                error: "Erro",
                skipped: "Ignorado",
            };
            return labels[status] || status;
        },

        itemStatusClass(status) {
            const classes = {
                pending: "inline-flex rounded bg-yellow-100 px-2 py-0.5 text-xs font-medium text-yellow-800 dark:bg-yellow-900/30 dark:text-yellow-200",
                success: "inline-flex rounded bg-green-100 px-2 py-0.5 text-xs font-medium text-green-800 dark:bg-green-900/30 dark:text-green-200",
                error: "inline-flex rounded bg-red-100 px-2 py-0.5 text-xs font-medium text-red-800 dark:bg-red-900/30 dark:text-red-200",
                skipped: "inline-flex rounded bg-gray-100 px-2 py-0.5 text-xs font-medium text-gray-800 dark:bg-gray-900/30 dark:text-gray-200",
            };
            return classes[status] || classes.pending;
        },
    },
};
</script>

